import 'requestidlecallback';
let marksCollection = [];
let nextMarkIndex = 0;
let marksMadeCbs = [];
let marksAddedCbs = [];
// There are cases in testing environments (jest) where
// window is removed before async processes complete.
// When attempting to reference window.location, Jest's
// internal getter will throw an error. To combat that
// in a performant manner, we will retain a reference to the
// initial window and let test resets to grab the set it
// to a new one
let windowReference = window;
let locationReference = window.location;
/**
 * Centralized adding of a mark to the collection. This flow
 * will start any processes that respond to new marks being added.
 *
 * @param mark the mark to add to the collection
 */
function addMark(mark) {
    marksCollection.push(mark);
    if (marksMadeCbs.length > 0) {
        requestIdleCallback(() => {
            marksMadeCbs = marksMadeCbs.filter(({ whenMadeCallback, markNamesToWaitFor, startOffset, verifyMark, }) => {
                const matchedMarks = checkMatchedMarks(markNamesToWaitFor, marksCollection, startOffset, verifyMark);
                if (matchedMarks) {
                    whenMadeCallback(matchedMarks);
                    return false;
                }
                return true;
            });
        }, { timeout: 2000 });
    }
    marksAddedCbs.forEach((cb) => cb(mark));
}
/**
 * Snapshot the current information about the environment
 * so they can be used later. Perf marks are processed asynchronously
 * and when they inspect the environment information, that info should
 * reflect the environment when it was marked not when it was processed.
 *
 * @returns the env information for a given mark
 */
function snapshotMarkEnv() {
    return {
        pageUrl: locationReference.href,
        pageIsIframed: windowReference.top !== windowReference.self,
    };
}
/**
 * @returns literally all of the marks
 */
export function getAllMarks() {
    return marksCollection;
}
/**
 * Only expected to be used for testing
 * @private
 */
export function resetRUMState() {
    marksCollection = [];
    nextMarkIndex = 0;
    marksMadeCbs = [];
    marksAddedCbs = [];
    windowReference = window;
    locationReference = window.location;
}
/**
 * Create a perf mark that denotes when something happened.
 * @param name what is this mark referring to?
 * @param props optional object that will be stored with the mark to provide additional context
 */
export function markTime(name, props) {
    // leverage performance.mark so that the metric can be surfaced in other
    // tooling like synthetic testing.
    let nativeMark = windowReference.performance.mark(name);
    // in some cases, we need to retroactively mark time.
    const overrideTimeMarked = props === null || props === void 0 ? void 0 : props.overrideTimeMarked;
    if (!nativeMark) {
        nativeMark = performance.getEntriesByName(name).pop();
    }
    const timeMarked = (!overrideTimeMarked || overrideTimeMarked === 0
        ? overrideTimeMarked
        : false) ||
        (nativeMark === null || nativeMark === void 0 ? void 0 : nativeMark.startTime) ||
        performance.now();
    const mark = {
        name,
        props,
        timeMarked,
        index: nextMarkIndex++,
        env: snapshotMarkEnv(),
    };
    addMark(mark);
    return mark;
}
/**
 * Create a mark that measures the time between two different marks.
 * @param measurementName what is this measurement mark referring to?
 * @param firstMark The mark that happened first in the order of events.
 * @param secondMark The mark to measure the time since
 */
export function markMeasurement(measurementName, firstMark, secondMark) {
    // leverage performance.mark so that the metric can be surfaced in other
    // tooling like synthetic testing.
    let nativeMark = windowReference.performance.mark(measurementName);
    if (!nativeMark) {
        nativeMark = performance.getEntriesByName(measurementName).pop();
    }
    const mark = {
        name: measurementName,
        duration: secondMark.timeMarked - firstMark.timeMarked,
        timeMarked: (nativeMark === null || nativeMark === void 0 ? void 0 : nativeMark.startTime) || performance.now(),
        index: nextMarkIndex++,
        env: snapshotMarkEnv(),
    };
    addMark(mark);
    return mark;
}
/**
 * Runs the the current marks to see if all of the marks have been made.
 * @param markNamesToWaitFor
 * @param marks
 * @param startOffset
 * @param verifyMark
 * @returns will return an object (keyed by mark name) of all of the matched marks
 * if all of the marks are matched. Otherwise it will return null
 */
function checkMatchedMarks(markNamesToWaitFor, marks, startOffset, verifyMark) {
    const matchedMarks = marks.reduce((aggregate, mark, index) => {
        if (startOffset !== undefined && index < startOffset) {
            return aggregate;
        }
        if (markNamesToWaitFor.includes(mark.name) &&
            (verifyMark ? verifyMark(mark) : true)) {
            aggregate[mark.name] = mark;
        }
        return aggregate;
    }, {});
    if (Object.keys(matchedMarks).length === markNamesToWaitFor.length) {
        return matchedMarks;
    }
    return null;
}
/**
 * Set up a listener to monitor for marks by name. When all of the passed mark names
 * have been observed in the marks collection, the callback will be invoked with an object
 * keyed with the mark name and values containing the respective mark.
 *
 * By default, this listener will inspect all marks starting from the beginning of the collection.
 * This allows for asynchronously setting up aggregate functions in a way that doesn't require
 * the functions to be defined before any marks can be defined. If you only care about
 * marks going forward you can pass in the start offset which is the index the reducer will
 * start the checks from.
 *
 * @param {WhenAllMarksMadeOptions} param0
 */
export function whenAllMarksMade({ markNamesToWaitFor, whenMadeCallback, startOffset, verifyMark, listenerClass, }) {
    const matchedMarks = checkMatchedMarks(markNamesToWaitFor, marksCollection, startOffset, verifyMark);
    if (matchedMarks) {
        requestIdleCallback(() => {
            whenMadeCallback(matchedMarks);
        }, { timeout: 2000 });
    }
    else {
        marksMadeCbs.push({
            whenMadeCallback,
            markNamesToWaitFor,
            startOffset,
            verifyMark,
            listenerClass,
        });
    }
}
/**
 * Clear out any pending whenAllMarksMade listeners by the listenerClass property.
 *
 * whenAllMarksMade allows assigning of a listener with a respective class name.
 * This allows for multiple listeners with the same function (but usually different contexts)
 * to wait for marks. This method allows for clean up of listeners across all contexts.
 *
 * @param {WhenAllMarksMadeOptions['listenerClass']} listenerClassToClear
 */
export function clearMarksMadeListenerByClass(listenerClassToClear) {
    marksMadeCbs = marksMadeCbs.filter(({ listenerClass }) => listenerClass !== listenerClassToClear);
}
export function onMarkAdded(markAddedCallback) {
    marksAddedCbs.push(markAddedCallback);
}
// for live debugging in the browser
// @ts-expect-error
window._rum = window._rum || {};
// @ts-expect-error
window._rum.markers = {
    getAllMarks,
    markTime,
    markMeasurement,
    whenAllMarksMade,
    onMarkAdded,
};
//# sourceMappingURL=index.js.map